import { IChip } from 'affiny-core/helpers/memberChoices';
import { default as React, memo } from 'react';
import styled from 'styled-components';
import Chip from 'affiny-ui/src/components/chip/chip';
import { Box, BoxProps, Flex } from '../primitives';
import ChipsListTitleWithSelection from './components/chips-list-title-with-selection';
import ChipsListTitleWithCommonInterest from './components/chips-list-title-with-common-interest';

type Props = Omit<BoxProps, 'color'> & {
  chips: IChip[];
  className?: string;
  isMultiChoice?: boolean;
  maxChoices?: number;
  otherGender?: any;
  readOnly?: boolean;
  title?: string;
  withCommonInterestsPhrase?: boolean;
};

const ChipsList = ({
  chips,
  className,
  isMultiChoice = false,
  maxChoices,
  otherGender,
  readOnly = true,
  title,
  withCommonInterestsPhrase = true,
}: Props) => {
  const commonCount = chips.reduce(
    (total, current) => (current.isInCommon ? total + 1 : total),
    0
  );

  if (!chips.length) return null;

  return (
    <Box className={className}>
      {title && (
        <>
          {readOnly ? (
            <SChipsListTitleWithCommonInterest
              commonCount={commonCount}
              otherGender={otherGender}
              title={title}
              withCommonInterestsPhrase={withCommonInterestsPhrase}
            />
          ) : (
            <ChipsListTitleWithSelection
              isMultiChoice={isMultiChoice}
              maxChoices={maxChoices}
              title={title}
            />
          )}
        </>
      )}

      <SFlex readOnly={readOnly}>
        {chips.map(({ label, isInCommon, isSelected, onClick }) => {
          let chipColor;

          if (readOnly) {
            if (isInCommon) chipColor = 'secondary';
          } else {
            chipColor = 'primary';
          }

          return (
            <Chip
              key={label}
              color={chipColor}
              clickable={!readOnly}
              isActive={!readOnly && isSelected}
              onClick={!readOnly ? onClick : undefined}
              text={label}
              variant={readOnly ? 'outlined' : undefined}
              readOnly={readOnly}
            />
          );
        })}
      </SFlex>
    </Box>
  );
};

const SChipsListTitleWithCommonInterest = styled(
  ChipsListTitleWithCommonInterest
)`
  margin-bottom: ${({ theme }) => theme.space.small};
`;

// https://codyhouse.co/nuggets/flex-gap-using-negative-margins
const SFlex = styled(Flex)<{ readOnly: boolean }>`
  flex-wrap: wrap;
  --gap: ${({ theme }) => theme.space.small};
  margin-bottom: ${({ readOnly }) =>
    readOnly ? 'calc(-1 * var(--gap))' : 'calc(-2 * var(--gap))'};
  margin-right: calc(-1 * var(--gap));
  & > div {
    margin-right: var(--gap);
    margin-bottom: ${({ readOnly }) =>
      readOnly ? 'var(--gap)' : 'calc(2 * var(--gap))'};
  }
`;

export default memo(ChipsList);
